import { createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Dialog, Typography } from '@nutrien/cxp-components';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../../mobx-models/Root';
import useComponentChecklist from '../../rxdb/componentChecklist/useComponentChecklist';
import useSavePmChecklist from '../../rxdb/savepmChecklist/useSavePmChecklist';
import useSelectedTask from '../../rxdb/taskList/useSelectedTask';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { useNotification } from '../../utilities/useNotification';
import { TaskStatus } from '../../utilities/enums';
import { getValueFromLocalStorage, isNull } from '../../utilities/utilityFunctions';
import TabBarConfig from './TabBarConfig.json';

const useStyles = makeStyles(() =>
  createStyles({
    buttonText: {
      letterSpacing: '1.25px',
      fontWeight: 700,
    },
    boxContent: {
      padding: '28px 0px',
      '& .SaveChangesText': {
        width: '100%',
        fontSize: 16,
        fontWeight: 300,
        letterSpacing: '0.5px',
        color: 'rgba(255, 255, 255, 0.87)',
      },
    },
  }),
);

interface Props {
  open: boolean;
  onClose: () => void;
  futurePath: number;
}

const ConfirmSavingDialog: React.FC<Props> = ({ open, onClose, futurePath }) => {
  const classes = useStyles();
  const { SaveChecklist, saveInspection, SavePMChecklist, CurrentUserSelected } = useMst();
  const history = useHistory();
  const { saveComponentChecklist } = useComponentChecklist();
  const { setTaskRecommendation } = useTaskList();
  const { savePmChecklistTransactionData } = useSavePmChecklist();
  const { successNotification } = useNotification();
  const { setTaskListInfo } = useTaskList();
  const [isSaving, setIsSaving] = useState(false);
  const componentCheckListPayload = SaveChecklist.checkListData;
  const PMChecklistPayload = SavePMChecklist.PMcheckListData;
  const { taskData } = useSelectedTask(CurrentUserSelected.taskId);

  const saveChecklistData = async () => {
    setIsSaving(true);
    if (SaveChecklist.isChangedTrue) {
      componentCheckListPayload?.map(async record => {
        if (record?.pk_uuid && record.isChanged) {
          await saveComponentChecklist({
            ...record,
            isChanged: undefined,
            fkInspectorLead: taskData?.fkInspectorLead,
            fkInspectorField: taskData?.fkInspectorField,
          });
        } else if (record.checked && record.isChanged) {
          await saveComponentChecklist({
            ...record,
            isDelete: 'False',
            isChanged: undefined,
            fkInspectorLead: taskData?.fkInspectorLead,
            fkInspectorField: taskData?.fkInspectorField,
          });
        }
      });
      if (
        taskData?.fkInspectionTaskStatusCategory !== TaskStatus.FI_SIGN_PENDING &&
        !CurrentUserSelected.isAdhoc
      ) {
        await setTaskListInfo(TaskStatus.IN_PROGRESS);
      }
    } else if (SavePMChecklist.isChangedTrue) {
      setIsSaving(true);
      PMChecklistPayload?.map(async record => {
        if (record?.isCompletedChange || record?.isCommentChange) {
          await savePmChecklistTransactionData({
            ...record,
            pk_uuid: isNull(record, 'pk_uuid', uuidv4()),
          });
        }
      });
      if (
        taskData?.fkInspectionTaskStatusCategory !== TaskStatus.FI_SIGN_PENDING &&
        !CurrentUserSelected.isAdhoc
      ) {
        await setTaskListInfo(TaskStatus.IN_PROGRESS);
      }
    } else if (saveInspection.isChangedTrue) {
      const { tempData } = saveInspection;
      const finalPayload = {
        dateCompleted: isNull(tempData, 'dateCompleted', new Date().toISOString()),
        recommendations: isNull(tempData, 'recommendations', ''),
      };
      await setTaskRecommendation(finalPayload);
    }
    setIsSaving(false);
    onClose();
    if (JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId === 1) {
      history.push(TabBarConfig?.itemsForElectrical[futurePath]?.path);
    } else {
      history.push(TabBarConfig?.items[futurePath]?.path);
    }
    if (SaveChecklist.isChangedTrue) {
      successNotification('Component Checklist Saved Successfully');
      SaveChecklist.setIsChanged(false);
    } else if (SavePMChecklist.isChangedTrue) {
      successNotification('PM Checklist Saved Successfully');
      SavePMChecklist.setIsChanged(false);
    } else if (saveInspection.isChangedTrue) {
      successNotification('Complete Inspection Saved Successfully');
      saveInspection.setIsChanged(false);
      saveInspection.setIsSaved(true);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      titleText="Confirm Saving"
      backButton={false}
      dialogActions={
        <Grid container justifyContent="space-between" spacing={2}>
          <Grid item>
            <Button
              className={classes.buttonText}
              variant="outlined"
              color="primary"
              noMinHeight
              onClick={onClose}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonText}
              noMinHeight
              isLoading={isSaving}
              onClick={saveChecklistData}
            >
              Yes, Save
            </Button>
          </Grid>
        </Grid>
      }
      maxWidth="xs"
      noMinHeight
    >
      <Grid container className={classes.boxContent}>
        <Grid item container xs={12} alignItems="center">
          <Typography className="SaveChangesText">
            {`If you leave before saving, your changes will be lost.
              Do you want to save the changes?`}
          </Typography>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ConfirmSavingDialog;
